import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  collection, query, where, orderBy, limit as fbLimit,
  onSnapshot, getDocs,
  doc, getDoc, serverTimestamp,
  increment as fbIncrement,
  runTransaction,
} from 'firebase/firestore';
import { db, auth, API_BASE_URL } from '../../firebase/firebaseConfig';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bell, ArrowLeft, X, Archive, Mail, MailOpen, AlertTriangle, RefreshCw, ChevronDown, EyeOff } from 'lucide-react';
import AlertTraceView from './AlertTraceView';

// Full-screen alerts viewer.
//
// Modes via the `familyId` prop:
//   - non-null: per-device — alerts for that one family
//   - null:     cross-family inbox — list of families with alerts, click to drill in
//
// Mirrors the supportConversations / MessageInbox pattern: badge counts come
// from the `unreadCount` field on the per-family parent doc
// `criticalAlerts/{familyId}`, maintained atomically via FieldValue.increment
// on the device side (AlertWriter) and the webapp side (mark-read/unread/
// archive). No collection-group queries, no composite indexes.
//
// Internal view states:
//   - cross-family + no drilled family → FamilyListView (parent docs)
//   - drilled family + no selected alert → AlertsListView (per-family alerts)
//   - selected alert → AlertDetailView (single alert with trace + actions)

function severityBadge(sev) {
  if (sev === 'error')   return <Badge variant="destructive">ERROR</Badge>;
  if (sev === 'warning') return <Badge className="bg-amber-500 hover:bg-amber-600">WARNING</Badge>;
  return <Badge variant="secondary">{(sev || '?').toUpperCase()}</Badge>;
}

function tsMs(t) {
  if (t == null) return 0;
  if (typeof t === 'number') return t;
  if (t.toDate) return t.toDate().getTime();
  if (t._seconds) return t._seconds * 1000;
  return 0;
}

function formatAbsTime(ms) {
  if (!ms) return 'N/A';
  return new Date(ms).toISOString().replace('T', ' ').substring(0, 19);
}

function formatAgo(ms) {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 60) return `${sec}s ago`;
  if (min < 60) return `${min}m ago`;
  if (hr  < 24) return `${hr}h ago`;
  return `${day}d ago`;
}

// Pretty-label the `screen` field stamped on alerts by the TVApp's AlertWriter.
// Values come from DataLogger system.screen and include: home, training,
// no_network, sleep, an iglooID (UUID-shaped), or an iglooID with -<position>
// suffix when inside an igloo. Older alerts (pre-this-feature) lack the field
// entirely — the caller is responsible for not rendering anything in that case.
function formatScreen(screen) {
  if (!screen || typeof screen !== 'string') return null;
  const known = { home: 'Home', training: 'Training', no_network: 'No network', sleep: 'Sleep', higlo_reset: 'Higlo reset' };
  if (known[screen]) return known[screen];
  // iglooID-shaped (UUID) — possibly with a -<position> suffix.
  const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;
  if (uuidLike.test(screen)) {
    const dashIdx = screen.indexOf('-', 36); // first dash after the UUID body
    const position = dashIdx > 0 ? screen.slice(dashIdx + 1) : null;
    return position ? `Igloo (${position})` : 'Igloo';
  }
  return screen;
}

function formatVersion(name, code) {
  if (!name && code == null) return null;
  if (!name) return `build ${code}`;
  return code != null ? `v${name} (${code})` : `v${name}`;
}

// Returns a string like "set 2s before alert" suitable for a title tooltip on
// the screen pill. Helps spot the brief sleep→wake gap where the screen field
// can be stale relative to the alert.
function screenStalenessTitle(alertTms, screenAtMs) {
  const a = tsMs(alertTms);
  const s = tsMs(screenAtMs);
  if (!a || !s) return null;
  const diffSec = Math.round((a - s) / 1000);
  if (diffSec <= 1) return 'screen value set just before alert';
  if (diffSec < 60) return `screen value set ${diffSec}s before alert`;
  if (diffSec < 3600) return `screen value set ${Math.round(diffSec / 60)}m before alert`;
  return `screen value set ${Math.round(diffSec / 3600)}h before alert — may be stale`;
}

// Lazy-resolve the owner email + device/family name for a familyId. If a value
// is passed in by the parent (e.g. drilled in from the inbox, which already
// has them), we skip the lookup and return the provided value.
function useFamilyIdentity(familyId, providedEmail, providedName) {
  const [email, setEmail] = useState(null);
  const [name, setName] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (familyId && !providedEmail) {
      getDocs(query(collection(db, 'accountsInfo'), where('families', 'array-contains', familyId)))
        .then((snap) => {
          if (!cancelled && !snap.empty) setEmail(snap.docs[0].data().email || null);
        })
        .catch(() => {});
    }
    if (familyId && !providedName) {
      getDoc(doc(db, 'familyInfo', familyId))
        .then((snap) => {
          if (!cancelled && snap.exists()) setName(snap.data().name || null);
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [familyId, providedEmail, providedName]);
  return { email: providedEmail || email, name: providedName || name };
}

// ────────────────────────────────────────────────────────────────────
// Shared mutations — used by both the per-alert detail view and the
// inline row buttons in the flat feed. Each is transacted against the
// parent-doc unreadCount so the badge tracks read state without drift:
// the transaction re-reads the alert doc and gates the increment on the
// *current* readAt (not a stale closure), so double-clicks and
// read-then-archive races can't double-count. Callers wrap in try/catch.
// ────────────────────────────────────────────────────────────────────

async function markAlertRead(alert) {
  if (!alert || alert.readAt) return;
  const alertRef = doc(db, 'criticalAlerts', alert.familyId, 'alerts', alert.id);
  const parentRef = doc(db, 'criticalAlerts', alert.familyId);
  await runTransaction(db, async (tx) => {
    const fresh = await tx.get(alertRef);
    if (!fresh.exists() || fresh.data().readAt) return;
    tx.update(alertRef, {
      readAt: Date.now(),
      readBy: auth.currentUser?.email || 'admin',
    });
    tx.set(parentRef, { unreadCount: fbIncrement(-1) }, { merge: true });
  });
}

async function markAlertUnread(alert) {
  if (!alert || !alert.readAt) return;
  const alertRef = doc(db, 'criticalAlerts', alert.familyId, 'alerts', alert.id);
  const parentRef = doc(db, 'criticalAlerts', alert.familyId);
  await runTransaction(db, async (tx) => {
    const fresh = await tx.get(alertRef);
    if (!fresh.exists() || !fresh.data().readAt) return;
    tx.update(alertRef, { readAt: null, readBy: null });
    tx.set(parentRef, { unreadCount: fbIncrement(1) }, { merge: true });
  });
}

async function archiveAlert(alert) {
  if (!alert) return;
  const srcRef = doc(db, 'criticalAlerts', alert.familyId, 'alerts', alert.id);
  const dstRef = doc(db, 'criticalAlertsHistory', alert.familyId, 'alerts', alert.id);
  const parentRef = doc(db, 'criticalAlerts', alert.familyId);
  await runTransaction(db, async (tx) => {
    const fresh = await tx.get(srcRef);
    if (!fresh.exists()) return;
    const data = fresh.data();
    tx.set(dstRef, { ...data, archivedAt: serverTimestamp() });
    tx.delete(srcRef);
    if (!data.readAt) {
      tx.set(parentRef, { unreadCount: fbIncrement(-1) }, { merge: true });
    }
  });
}

// Family-level bulk ops are handled server-side (admin cloud function) so they
// cover every alert in the subcollection — not just the ~200 the client loaded
// — and reset the parent unreadCount atomically. See adminEndpoints.js
// /clearFamilyAlerts. mode is 'read' (mark all unread as read) or 'archive'
// (move every alert to history).
async function clearFamilyAlerts(familyId, mode) {
  const idToken = await auth.currentUser.getIdToken(true);
  const res = await fetch(`${API_BASE_URL}/admin/clearFamilyAlerts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ familyId, mode }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`clearFamilyAlerts ${mode} failed: ${res.status} ${text}`);
  }
  return res.json();
}

// Segmented toggle between the flat cross-family feed and the grouped-by-
// family inbox. Rendered in the header of both cross-family root views.
function ModeToggle({ mode, onChange }) {
  const opt = (val, label) => (
    <button
      onClick={() => onChange(val)}
      className={`px-3 py-1 text-sm transition-colors ${
        mode === val ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center rounded-md border border-gray-200 overflow-hidden">
      {opt('flat', 'All alerts')}
      {opt('family', 'By family')}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Alert type filtering — visibility model. The "type" of an alert is the
// dotted `subsystem.reason` pair. A single persisted set of *hidden* types
// (empty = everything shown) is the one source of truth; every control — the
// grouped type dropdown, the per-subsystem hide/show-all, and the per-row hide
// button — reads and writes it. The set persists across reloads in
// localStorage so recurring noise stays muted, and a "N types hidden · Show
// all" indicator keeps that state visible and reversible. Cross-subsystem by
// design. Shared by the flat feed and the per-family list.
// ────────────────────────────────────────────────────────────────────

const HIDDEN_TYPES_STORAGE_KEY = 'higlo.admin.alertHiddenTypes';

// A type key is `subsystem.reason`. subsystem/reason are single dotless tokens
// (EventLog convention), so the first dot always separates them.
const typeKey = (subsystem, reason) => `${subsystem}.${reason}`;

// Normalised key for a specific alert — the single place the empty/missing
// fallback is applied, so the grouped dropdown toggle, `filtered`, and the
// per-row hide button all agree on the same key for a given alert (a malformed
// alert with no subsystem/reason otherwise gets `(unknown)` in one place and
// `undefined` in another, desyncing the switch from the actual filter).
const alertTypeKey = (a) => typeKey(a.subsystem || '(unknown)', a.reason || '(unknown)');

function loadHiddenTypes() {
  try {
    const raw = localStorage.getItem(HIDDEN_TYPES_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveHiddenTypes(arr) {
  try {
    localStorage.setItem(HIDDEN_TYPES_STORAGE_KEY, JSON.stringify(arr));
  } catch {
    /* storage disabled or over quota — the hidden set just won't persist */
  }
}

// Presentational on/off switch. Not interactive itself — the row that contains
// it is the button, so we never nest interactive controls (this mirrors the look
// of ui/switch.jsx, which is a `<button role="switch">` and so can't be nested
// inside the row button). On (blue) = the type is shown; off (grey) = hidden.
function SwitchVisual({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-blue-600' : 'bg-gray-300'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0.5'
        )}
      />
    </span>
  );
}

// Grouped alert-type dropdown. No popover primitive exists in ui/, so this is
// self-contained: a SelectTrigger-styled button opening an absolutely-
// positioned panel of subsystem groups. Each group has a header (subsystem +
// total count + a Hide-all/Show-all that flips the whole subsystem) and one
// switch row per reason (on = shown, off = hidden) with its count on the right.
// Cross-subsystem by design — silence any mix of types at once. Outside-click
// and element-level Escape close it (the latter stops the native event so it
// doesn't also trip the viewer's document-level Escape handler).
function AlertTypeFilter({
  groups,
  hiddenSet,
  hiddenCount,
  onToggle,
  onHideKeys,
  onShowKeys,
  onShowAll,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  // Clear the search box when the panel closes; focus it when it opens.
  useEffect(() => {
    if (!open) { setSearch(''); return undefined; }
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // While open, close on outside mousedown and on Escape. Escape is handled at
  // the document level in the *capture* phase and registered only while open, so
  // it reliably fires before the viewer's bubble-phase document Escape handler
  // no matter where focus sits (even tabbed out of the panel); stopping
  // propagation then keeps the viewer's handler (detail → list → drill-up →
  // close) from also running. When the panel is closed this listener isn't
  // registered, so Escape reaches the viewer as usual — that "only while open"
  // scoping is what keeps this from behaving like the pre-fix always-on version.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyCapture = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKeyCapture, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKeyCapture, true);
    };
  }, [open]);

  // Nothing to configure when there are no types at all (no loaded alerts and
  // nothing hidden). Force closed so aria-expanded never reports true on a
  // collapsed, disabled trigger.
  const isDisabled = groups.length === 0;
  useEffect(() => {
    if (isDisabled) setOpen(false);
  }, [isDisabled]);

  const q = search.trim().toLowerCase();
  const totalOptions = useMemo(
    () => groups.reduce((s, g) => s + g.reasons.length, 0),
    [groups]
  );
  const showSearch = totalOptions > 8;

  // While searching, keep only groups with a matching reason. Matched on the
  // full `subsystem.reason` key, so you can search by either part.
  const visibleGroups = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, reasons: g.reasons.filter((r) => r.key.toLowerCase().includes(q)) }))
      .filter((g) => g.reasons.length > 0);
  }, [groups, q]);

  // The panel-top control is a single global toggle (every type, ignoring any
  // active search): "Hide all" while nothing is hidden, flipping to "Show all"
  // the moment anything is hidden — so from a mostly-shown state the offered
  // action is "restore", not "hide the rest". `allKeys` spans all groups.
  const allKeys = useMemo(() => groups.flatMap((g) => g.reasons.map((r) => r.key)), [groups]);

  return (
    <div ref={rootRef} className="relative w-[170px]">
      <button
        type="button"
        disabled={isDisabled}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="line-clamp-1 text-left">Alert types</span>
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
      </button>
      {open && !isDisabled && (
        <div
          role="group"
          aria-label="Filter alert types"
          className="absolute z-50 mt-1 w-max min-w-full max-w-[420px] max-h-96 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md p-1"
        >
          {showSearch && (
            <div className="sticky top-0 z-10 bg-popover px-1 pt-1 pb-1">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter types…"
                className="w-full h-8 rounded-sm border border-input bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
          <div className="flex items-center justify-between gap-3 px-2 py-1.5">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {hiddenCount === 0 ? 'All types shown' : `${hiddenCount} hidden`}
            </span>
            {hiddenCount === 0 ? (
              <button
                type="button"
                onClick={() => onHideKeys(allKeys)}
                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
              >
                Hide all
              </button>
            ) : (
              <button
                type="button"
                onClick={onShowAll}
                className="text-xs text-blue-600 hover:underline whitespace-nowrap"
              >
                Show all
              </button>
            )}
          </div>
          <div className="-mx-1 my-1 h-px bg-muted" />
          {visibleGroups.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              No types match “{search.trim()}”
            </div>
          ) : (
            visibleGroups.map((g) => {
              const keys = g.reasons.map((r) => r.key);
              // Single toggle, same rule as the panel-top control: flip to "Show
              // all" the moment ANY reason in this subsystem is hidden, so the
              // offered action from a mostly-shown group is "restore".
              const anyHidden = keys.some((k) => hiddenSet.has(k));
              return (
                <div key={g.subsystem} className="py-1">
                  <div className="flex items-center justify-between gap-3 px-2 py-1">
                    <span className="text-xs font-semibold text-gray-700 truncate">
                      {g.subsystem}{' '}
                      <span className="font-normal text-muted-foreground">({g.count})</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => (anyHidden ? onShowKeys(keys) : onHideKeys(keys))}
                      className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                    >
                      {anyHidden ? 'Show all' : 'Hide all'}
                    </button>
                  </div>
                  {g.reasons.map((r) => {
                    const visible = !hiddenSet.has(r.key);
                    return (
                      <button
                        type="button"
                        key={r.key}
                        role="switch"
                        aria-checked={visible}
                        aria-label={r.key}
                        onClick={() => onToggle(r.key)}
                        className="flex w-full items-center gap-2 rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                      >
                        <SwitchVisual checked={visible} />
                        <span className={cn('flex-1 min-w-0 text-left truncate', !visible && 'text-muted-foreground line-through')}>
                          {r.reason}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">{r.count}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Preset time windows for the alert feed. `weeks: 0` = no lower bound (all
// loaded). Drives both the range <Select> options and the cutoff math, so the
// two never drift.
const RANGE_OPTIONS = [
  { value: 'all', label: 'All time',     weeks: 0 },
  { value: '2w',  label: 'Last 2 weeks', weeks: 2 },
  { value: '3w',  label: 'Last 3 weeks', weeks: 3 },
  { value: '4w',  label: 'Last 4 weeks', weeks: 4 },
];
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Owns the alert filters (time range, read, severity) plus the persisted
// hidden-type set, and derives the grouped type options + counts, so both
// views stay thin. Counts are computed over `base` — the alerts already
// passing time-range+read+severity — so a type's count reflects what hiding it
// would remove. `filtered` is `base` minus any hidden type.
function useAlertFilters(alerts) {
  const [readFilter, setReadFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [hidden, setHidden] = useState(loadHiddenTypes); // array of type keys
  const [rangeFilter, setRangeFilter] = useState('all'); // preset time window; see RANGE_OPTIONS

  // Persist the hidden set so recurring noise stays muted across reloads.
  useEffect(() => { saveHiddenTypes(hidden); }, [hidden]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);

  const base = useMemo(() => {
    const weeks = RANGE_OPTIONS.find((o) => o.value === rangeFilter)?.weeks ?? 0;
    const cutoff = weeks > 0 ? Date.now() - weeks * WEEK_MS : 0;
    return alerts.filter((a) => {
      if (readFilter === 'unread' && a.readAt) return false;
      if (readFilter === 'read' && !a.readAt) return false;
      if (severityFilter !== 'all' && a.severity !== severityFilter) return false;
      if (cutoff > 0 && tsMs(a.tMs) < cutoff) return false;
      return true;
    });
  }, [alerts, readFilter, severityFilter, rangeFilter]);

  // Types present in `base`, grouped by subsystem with per-reason counts. Any
  // currently-hidden type that isn't firing right now is merged in with count 0,
  // so every hidden type stays individually un-hideable from the list —
  // otherwise it could get stuck hidden, recoverable only via "Show all".
  const typeGroups = useMemo(() => {
    const groups = new Map(); // subsystem -> Map(reason -> count)
    const ensure = (sub, rsn) => {
      if (!groups.has(sub)) groups.set(sub, new Map());
      const rm = groups.get(sub);
      if (!rm.has(rsn)) rm.set(rsn, 0);
      return rm;
    };
    base.forEach((a) => {
      const sub = a.subsystem || '(unknown)';
      const rsn = a.reason || '(unknown)';
      const rm = ensure(sub, rsn);
      rm.set(rsn, rm.get(rsn) + 1);
    });
    hidden.forEach((key) => {
      const dot = key.indexOf('.');
      if (dot < 0) return;
      ensure(key.slice(0, dot), key.slice(dot + 1));
    });
    return Array.from(groups.entries())
      .sort((x, y) => x[0].localeCompare(y[0]))
      .map(([subsystem, rm]) => {
        const reasons = Array.from(rm.entries())
          .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
          .map(([reason, count]) => ({ reason, count, key: typeKey(subsystem, reason) }));
        const count = reasons.reduce((s, r) => s + r.count, 0);
        return { subsystem, count, reasons };
      });
  }, [base, hidden]);

  const filtered = useMemo(
    () => base.filter((a) => !hiddenSet.has(alertTypeKey(a))),
    [base, hiddenSet]
  );

  const hideType = useCallback((key) => {
    setHidden((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);
  const toggleType = useCallback((key) => {
    setHidden((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);
  const hideKeys = useCallback((keys) => {
    setHidden((prev) => Array.from(new Set([...prev, ...keys])));
  }, []);
  const showKeys = useCallback((keys) => {
    const drop = new Set(keys);
    setHidden((prev) => prev.filter((k) => !drop.has(k)));
  }, []);
  const showAll = useCallback(() => setHidden([]), []);

  return {
    rangeFilter, setRangeFilter,
    readFilter, setReadFilter,
    severityFilter, setSeverityFilter,
    hiddenSet, hiddenCount: hidden.length,
    typeGroups,
    hideType, toggleType, hideKeys, showKeys, showAll,
    filtered,
  };
}

// Renders the filter bar (time range · read · severity · alert-type dropdown ·
// "N hidden" indicator) from a useAlertFilters() object, plus a right-aligned
// `trailing` slot for each view's own summary text.
function AlertFilterBar({ f, trailing }) {
  return (
    <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
      <Select value={f.rangeFilter} onValueChange={f.setRangeFilter}>
        <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {RANGE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={f.readFilter} onValueChange={f.setReadFilter}>
        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All (read+unread)</SelectItem>
          <SelectItem value="unread">Unread only</SelectItem>
          <SelectItem value="read">Read only</SelectItem>
        </SelectContent>
      </Select>
      <Select value={f.severityFilter} onValueChange={f.setSeverityFilter}>
        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All severities</SelectItem>
          <SelectItem value="error">Error</SelectItem>
          <SelectItem value="warning">Warning</SelectItem>
        </SelectContent>
      </Select>
      <AlertTypeFilter
        groups={f.typeGroups}
        hiddenSet={f.hiddenSet}
        hiddenCount={f.hiddenCount}
        onToggle={f.toggleType}
        onHideKeys={f.hideKeys}
        onShowKeys={f.showKeys}
        onShowAll={f.showAll}
      />
      {f.hiddenCount > 0 && (
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{f.hiddenCount} type{f.hiddenCount === 1 ? '' : 's'} hidden</span>
          <button type="button" onClick={f.showAll} className="text-blue-600 hover:underline">
            Show all
          </button>
        </span>
      )}
      {trailing}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Top-level container
// ────────────────────────────────────────────────────────────────────

function AlertsViewer({ familyId = null, familyName = null, familyEmail = null, onClose }) {
  // Cross-family root view: flat chronological feed (default) or grouped by
  // family. Ignored when opened for a single device (familyId prop set).
  const [mode, setMode] = useState('flat');
  const [drilledFamilyId, setDrilledFamilyId] = useState(null);
  const [drilledFamilyName, setDrilledFamilyName] = useState(null);
  const [drilledFamilyEmail, setDrilledFamilyEmail] = useState(null);
  // Selected alert for the detail view. Carries its own familyId so an alert
  // picked from the flat feed (no drilled family) still resolves its doc.
  const [selected, setSelected] = useState(null);

  const activeFamilyId = familyId ?? drilledFamilyId;
  const activeFamilyName = familyName ?? drilledFamilyName;
  const activeFamilyEmail = familyEmail ?? drilledFamilyEmail;

  const clearDrill = () => {
    setDrilledFamilyId(null);
    setDrilledFamilyName(null);
    setDrilledFamilyEmail(null);
  };

  // Escape key: detail → list/feed → drill-up → close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (selected) setSelected(null);
      else if (drilledFamilyId) clearDrill();
      else onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected, drilledFamilyId, onClose]);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {selected ? (
        <AlertDetailView
          alertId={selected.alertId}
          familyId={selected.familyId}
          familyName={selected.familyName}
          familyEmail={selected.familyEmail}
          onBack={() => setSelected(null)}
        />
      ) : activeFamilyId ? (
        <AlertsListView
          familyId={activeFamilyId}
          familyName={activeFamilyName}
          familyEmail={activeFamilyEmail}
          onSelect={(alertId) =>
            setSelected({
              alertId,
              familyId: activeFamilyId,
              familyName: activeFamilyName,
              familyEmail: activeFamilyEmail,
            })
          }
          onBack={drilledFamilyId ? clearDrill : null}
          onClose={onClose}
        />
      ) : mode === 'family' ? (
        <FamilyListView
          mode={mode}
          onModeChange={setMode}
          onPickFamily={(id, name, email) => {
            setDrilledFamilyId(id);
            setDrilledFamilyName(name);
            setDrilledFamilyEmail(email);
          }}
          onClose={onClose}
        />
      ) : (
        <AlertsFeedView
          mode={mode}
          onModeChange={setMode}
          onSelectAlert={(alert, name, email) =>
            setSelected({
              alertId: alert.id,
              familyId: alert.familyId,
              familyName: name ?? null,
              familyEmail: email ?? null,
            })
          }
          onPickFamily={(id, name, email) => {
            setDrilledFamilyId(id);
            setDrilledFamilyName(name);
            setDrilledFamilyEmail(email);
          }}
          onClose={onClose}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Cross-family inbox: list of families with unread alerts
// ────────────────────────────────────────────────────────────────────

function FamilyListView({ mode, onModeChange, onPickFamily, onClose }) {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [familyIdToEmail, setFamilyIdToEmail] = useState({});
  const [familyIdToName, setFamilyIdToName] = useState({});
  const nameFetchedRef = useRef(new Set());

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, 'criticalAlerts'),
      (snap) => {
        const list = [];
        snap.forEach((d) => list.push({ familyId: d.id, ...d.data() }));
        list.sort((a, b) => tsMs(b.lastAlertAt) - tsMs(a.lastAlertAt));
        setParents(list);
        setLoading(false);
      },
      (err) => {
        console.error('FamilyListView: listener error', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // One-shot: build familyId → owner email map from accountsInfo.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'accountsInfo'));
        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          if (!data.email) return;
          (data.families || []).forEach((fid) => { map[fid] = data.email; });
        });
        if (!cancelled) setFamilyIdToEmail(map);
      } catch (e) {
        console.error('FamilyListView: failed to load accountsInfo', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lazy: fetch familyInfo.name for every familyId that turns up.
  useEffect(() => {
    parents.forEach((p) => {
      if (nameFetchedRef.current.has(p.familyId)) return;
      nameFetchedRef.current.add(p.familyId);
      getDoc(doc(db, 'familyInfo', p.familyId))
        .then((snap) => {
          const name = snap.exists() ? (snap.data().name || null) : null;
          setFamilyIdToName((prev) => ({ ...prev, [p.familyId]: name }));
        })
        .catch(() => {
          setFamilyIdToName((prev) => ({ ...prev, [p.familyId]: null }));
        });
    });
  }, [parents]);

  const totalUnread = useMemo(
    () => parents.reduce((s, p) => s + (p.unreadCount || 0), 0),
    [parents]
  );

  const visible = useMemo(
    () => showAll ? parents : parents.filter((p) => (p.unreadCount || 0) > 0),
    [parents, showAll]
  );

  return (
    <>
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-gray-700" />
          <h1 className="text-xl font-semibold text-gray-900">Critical Alerts Inbox</h1>
          {totalUnread > 0 && (
            <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">
              {totalUnread > 99 ? '99+' : totalUnread} unread
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ModeToggle mode={mode} onChange={onModeChange} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Show families with no unread
          </label>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50">
        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400">
            <AlertTriangle className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-base">
              {showAll ? 'No families have ever fired an alert.' : 'No families with unread alerts.'}
            </p>
          </div>
        )}
        {!loading && visible.length > 0 && (
          <div className="divide-y divide-gray-100">
            {visible.map((p) => {
              const unread = p.unreadCount || 0;
              const tLast = tsMs(p.lastAlertAt);
              const email = familyIdToEmail[p.familyId] || null;
              const name = familyIdToName[p.familyId] || null;
              return (
                <button
                  key={p.familyId}
                  onClick={() => onPickFamily(p.familyId, name, email)}
                  className={`w-full text-left px-6 py-4 flex items-center gap-4 hover:bg-gray-100 active:bg-gray-200 transition-colors ${
                    unread > 0 ? 'bg-amber-50' : 'bg-white'
                  }`}
                >
                  <span className="flex-shrink-0">
                    {unread > 0
                      ? <Mail className="w-5 h-5 text-amber-700" />
                      : <MailOpen className="w-5 h-5 text-gray-400" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    {email || name ? (
                      <span className="block font-medium text-gray-900 truncate">
                        {email && <span>{email}</span>}
                        {email && name && <span className="text-gray-400"> — </span>}
                        {name && <span>{name}</span>}
                      </span>
                    ) : (
                      <span className="block font-medium text-gray-400 italic truncate">
                        Unknown account
                      </span>
                    )}
                    <span className="block font-mono text-[11px] text-gray-400 truncate">{p.familyId}</span>
                    <span className="block text-sm text-muted-foreground truncate">
                      {p.lastSeverity && p.lastSubsystem && p.lastReason
                        ? `Last: ${p.lastSeverity} · ${p.lastSubsystem}.${p.lastReason}`
                        : 'No alert details'}
                      {tLast > 0 && <> · {formatAgo(tLast)}</>}
                    </span>
                  </span>
                  {unread > 0 && (
                    <Badge variant="destructive" className="flex-shrink-0">
                      {unread > 99 ? '99+' : unread} unread
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Cross-family flat feed: every alert across families, newest first
// ────────────────────────────────────────────────────────────────────

const MAX_FEED_FAMILIES = 60; // cap on per-family alert listeners
const PER_FAMILY_LIMIT = 50;  // recent alerts pulled from each family
const FEED_RENDER_CAP = 500;  // max rows rendered into the DOM

function AlertsFeedView({ mode, onModeChange, onSelectAlert, onPickFamily, onClose }) {
  const [parents, setParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(true);
  const [familyIdToEmail, setFamilyIdToEmail] = useState({});
  const [familyIdToName, setFamilyIdToName] = useState({});
  const [alertsByFamily, setAlertsByFamily] = useState({});
  const [busyId, setBusyId] = useState(null);
  const nameFetchedRef = useRef(new Set());
  const unsubsRef = useRef({});

  // Parent docs: the families that have ever fired an alert, sorted by
  // recency. One cheap listener; drives which subcollections we fan out to.
  useEffect(() => {
    setLoadingParents(true);
    const unsub = onSnapshot(
      collection(db, 'criticalAlerts'),
      (snap) => {
        const list = [];
        snap.forEach((d) => list.push({ familyId: d.id, ...d.data() }));
        list.sort((a, b) => tsMs(b.lastAlertAt) - tsMs(a.lastAlertAt));
        setParents(list);
        setLoadingParents(false);
      },
      (err) => {
        console.error('AlertsFeedView: parent listener error', err);
        setLoadingParents(false);
      }
    );
    return () => unsub();
  }, []);

  // One-shot: familyId → owner email.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'accountsInfo'));
        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          if (!data.email) return;
          (data.families || []).forEach((fid) => { map[fid] = data.email; });
        });
        if (!cancelled) setFamilyIdToEmail(map);
      } catch (e) {
        console.error('AlertsFeedView: failed to load accountsInfo', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lazy: familyInfo.name for each familyId seen.
  useEffect(() => {
    parents.forEach((p) => {
      if (nameFetchedRef.current.has(p.familyId)) return;
      nameFetchedRef.current.add(p.familyId);
      getDoc(doc(db, 'familyInfo', p.familyId))
        .then((snap) => {
          const name = snap.exists() ? (snap.data().name || null) : null;
          setFamilyIdToName((prev) => ({ ...prev, [p.familyId]: name }));
        })
        .catch(() => {
          setFamilyIdToName((prev) => ({ ...prev, [p.familyId]: null }));
        });
    });
  }, [parents]);

  // Fan-out: one alerts-subcollection listener per family, capped to the most
  // recently-active MAX_FEED_FAMILIES. Keyed on the joined id list so the
  // diff only re-runs when the windowed set actually changes.
  const feedKey = useMemo(
    () => parents.slice(0, MAX_FEED_FAMILIES).map((p) => p.familyId).join(','),
    [parents]
  );
  useEffect(() => {
    const wantedIds = feedKey ? feedKey.split(',') : [];
    const wanted = new Set(wantedIds);
    const subs = unsubsRef.current;
    Object.keys(subs).forEach((fid) => {
      if (!wanted.has(fid)) {
        subs[fid]();
        delete subs[fid];
        setAlertsByFamily((prev) => {
          if (!(fid in prev)) return prev;
          const next = { ...prev };
          delete next[fid];
          return next;
        });
      }
    });
    wantedIds.forEach((fid) => {
      if (subs[fid]) return;
      const q = query(
        collection(db, 'criticalAlerts', fid, 'alerts'),
        orderBy('tMs', 'desc'),
        fbLimit(PER_FAMILY_LIMIT)
      );
      subs[fid] = onSnapshot(
        q,
        (snap) => {
          const list = [];
          snap.forEach((d) => list.push({ id: d.id, familyId: fid, ...d.data() }));
          setAlertsByFamily((prev) => ({ ...prev, [fid]: list }));
        },
        (err) => console.error('AlertsFeedView: alerts listener error', fid, err)
      );
    });
  }, [feedKey]);

  // Tear down every listener on unmount.
  useEffect(() => () => {
    Object.values(unsubsRef.current).forEach((fn) => fn());
    unsubsRef.current = {};
  }, []);

  const allAlerts = useMemo(() => {
    const merged = [];
    Object.values(alertsByFamily).forEach((list) => { merged.push(...list); });
    merged.sort((a, b) => tsMs(b.tMs) - tsMs(a.tMs));
    return merged;
  }, [alertsByFamily]);

  const f = useAlertFilters(allAlerts);
  const filtered = f.filtered;

  const totalUnread = useMemo(
    () => parents.reduce((s, p) => s + (p.unreadCount || 0), 0),
    [parents]
  );
  const windowed = Math.min(parents.length, MAX_FEED_FAMILIES);
  const familiesHidden = parents.length - windowed;
  const renderRows = filtered.slice(0, FEED_RENDER_CAP);
  const feedLoading = loadingParents || (parents.length > 0 && Object.keys(alertsByFamily).length === 0);

  const runAction = useCallback(async (alert, fn) => {
    setBusyId(alert.familyId + '/' + alert.id);
    try {
      await fn(alert);
    } catch (e) {
      console.error('AlertsFeedView: action failed', e);
    } finally {
      setBusyId(null);
    }
  }, []);

  return (
    <>
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-gray-700" />
          <h1 className="text-xl font-semibold text-gray-900">Critical Alerts</h1>
          {totalUnread > 0 && (
            <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">
              {totalUnread > 99 ? '99+' : totalUnread} unread
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ModeToggle mode={mode} onChange={onModeChange} />
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      <AlertFilterBar
        f={f}
        trailing={(
          <span className="text-xs text-muted-foreground ml-auto">
            {filtered.length} alert{filtered.length === 1 ? '' : 's'}
            {familiesHidden > 0 && ` · ${windowed} most-recent families (${familiesHidden} older hidden)`}
          </span>
        )}
      />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        {feedLoading && allAlerts.length === 0 && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        )}
        {!feedLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400">
            <AlertTriangle className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-base">No alerts match the current filters.</p>
          </div>
        )}
        {renderRows.length > 0 && (
          <div className="divide-y divide-gray-100">
            {renderRows.map((alert) => {
              const t = tsMs(alert.tMs);
              const isUnread = !alert.readAt;
              const email = familyIdToEmail[alert.familyId] || null;
              const name = familyIdToName[alert.familyId] || null;
              const busy = busyId === alert.familyId + '/' + alert.id;
              return (
                <div
                  key={alert.familyId + '/' + alert.id}
                  onClick={() => onSelectAlert(alert, name, email)}
                  className={`w-full text-left px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition-colors ${
                    isUnread ? 'bg-amber-50' : 'bg-white'
                  }`}
                >
                  <span className="flex-shrink-0">{severityBadge(alert.severity)}</span>
                  <span className="flex-shrink-0" title={isUnread ? 'Unread' : 'Read'}>
                    {isUnread
                      ? <Mail className="w-4 h-4 text-amber-700" />
                      : <MailOpen className="w-4 h-4 text-gray-400" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">
                      {alert.subsystem}.{alert.reason}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); onPickFamily(alert.familyId, name, email); }}
                      title="View this family's alerts"
                      className="block max-w-full truncate text-left text-xs text-blue-600 hover:underline"
                    >
                      {email || name ? (
                        <>
                          {email}
                          {email && name && <span className="text-gray-400"> · </span>}
                          {name}
                          <span className="font-mono text-gray-400"> · {alert.familyId}</span>
                        </>
                      ) : (
                        <span className="font-mono">{alert.familyId}</span>
                      )}
                    </button>
                    <span className="block text-xs text-muted-foreground truncate">
                      {formatAbsTime(t)} · {formatAgo(t)}
                      {formatScreen(alert.screen) && (
                        <span className="ml-2" title={screenStalenessTitle(alert.tMs, alert.screenAtMs) || undefined}>
                          · {formatScreen(alert.screen)}
                        </span>
                      )}
                      {formatVersion(alert.appVersionName, alert.appVersionCode) && (
                        <span className="ml-2 text-gray-400">
                          · {formatVersion(alert.appVersionName, alert.appVersionCode)}
                        </span>
                      )}
                      {alert.suppressedCount > 0 && (
                        <span className="ml-2 text-amber-700">+{alert.suppressedCount} suppressed</span>
                      )}
                    </span>
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={busy}
                      title={isUnread ? 'Mark as read' : 'Mark as unread'}
                      onClick={(e) => {
                        e.stopPropagation();
                        runAction(alert, isUnread ? markAlertRead : markAlertUnread);
                      }}
                    >
                      {isUnread
                        ? <MailOpen className="w-4 h-4" />
                        : <Mail className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900"
                      disabled={busy}
                      title="Archive"
                      onClick={(e) => {
                        e.stopPropagation();
                        runAction(alert, archiveAlert);
                      }}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900"
                      title={`Hide all "${alertTypeKey(alert)}" alerts`}
                      onClick={(e) => {
                        e.stopPropagation();
                        f.hideType(alertTypeKey(alert));
                      }}
                    >
                      <EyeOff className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            {filtered.length > renderRows.length && (
              <div className="px-6 py-3 text-center text-xs text-muted-foreground">
                Showing first {renderRows.length} of {filtered.length} — narrow with filters to see more.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Per-family alerts list
// ────────────────────────────────────────────────────────────────────

function AlertsListView({ familyId, familyName, familyEmail, onSelect, onBack, onClose }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(null); // null | 'read' | 'archive'
  const { email: effEmail, name: effName } = useFamilyIdentity(familyId, familyEmail, familyName);

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'criticalAlerts', familyId, 'alerts'),
      orderBy('tMs', 'desc'),
      fbLimit(200)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, familyId, ...d.data() }));
        setAlerts(list);
        setLoading(false);
      },
      (err) => {
        console.error('AlertsListView: listener error', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [familyId]);

  const f = useAlertFilters(alerts);
  const filtered = f.filtered;

  const counts = useMemo(() => {
    const total = alerts.length;
    const unread = alerts.filter((a) => !a.readAt).length;
    return { total, unread, read: total - unread };
  }, [alerts]);

  const handleMarkAllRead = useCallback(async () => {
    setBulkBusy('read');
    try {
      await clearFamilyAlerts(familyId, 'read');
    } catch (e) {
      console.error('AlertsListView: mark all read failed', e);
      window.alert('Failed to mark all as read — see console for details.');
    } finally {
      setBulkBusy(null);
    }
  }, [familyId]);

  const handleArchiveAll = useCallback(async () => {
    if (!window.confirm('Archive ALL alerts for this family? They will be moved to history and removed from this list.')) return;
    setBulkBusy('archive');
    try {
      await clearFamilyAlerts(familyId, 'archive');
      onBack?.();
    } catch (e) {
      console.error('AlertsListView: archive all failed', e);
      window.alert('Failed to archive all alerts — see console for details.');
    } finally {
      setBulkBusy(null);
    }
  }, [familyId, onBack]);

  return (
    <>
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <Bell className="w-6 h-6 text-gray-700 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 truncate">
              Alerts — {effEmail || effName
                ? (
                    <>
                      {effEmail}
                      {effEmail && effName && <span className="text-gray-400"> · </span>}
                      {effName}
                    </>
                  )
                : familyId}
            </h1>
            {(effEmail || effName) && (
              <p className="text-[11px] font-mono text-gray-400 truncate">{familyId}</p>
            )}
          </div>
          <span className="text-sm text-muted-foreground flex-shrink-0">
            {counts.unread} unread · {counts.read} read · {counts.total} total
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            disabled={bulkBusy !== null || counts.unread === 0}
            onClick={handleMarkAllRead}
            title="Mark every alert for this family as read"
          >
            <MailOpen className="w-4 h-4 mr-1" />
            {bulkBusy === 'read' ? 'Marking…' : 'Mark all read'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={bulkBusy !== null || counts.total === 0}
            onClick={handleArchiveAll}
            title="Archive every alert for this family"
          >
            <Archive className="w-4 h-4 mr-1" />
            {bulkBusy === 'archive' ? 'Archiving…' : 'Archive all'}
          </Button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      <AlertFilterBar
        f={f}
        trailing={(
          <span className="text-xs text-muted-foreground ml-auto">
            {filtered.length} match{filtered.length === 1 ? '' : 'es'}
          </span>
        )}
      />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400">
            <AlertTriangle className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-base">No alerts match the current filters.</p>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="divide-y divide-gray-100">
            {filtered.map((alert) => {
              const t = tsMs(alert.tMs);
              const isUnread = !alert.readAt;
              return (
                <div
                  key={alert.id}
                  onClick={() => onSelect(alert.id)}
                  className={`w-full text-left px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition-colors ${
                    isUnread ? 'bg-amber-50' : 'bg-white'
                  }`}
                >
                  <span className="flex-shrink-0">{severityBadge(alert.severity)}</span>
                  <span className="flex-shrink-0" title={isUnread ? 'Unread' : 'Read'}>
                    {isUnread
                      ? <Mail className="w-4 h-4 text-amber-700" />
                      : <MailOpen className="w-4 h-4 text-gray-400" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">
                      {alert.subsystem}.{alert.reason}
                    </span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {formatAbsTime(t)} · {formatAgo(t)}
                      {formatScreen(alert.screen) && (
                        <span className="ml-2" title={screenStalenessTitle(alert.tMs, alert.screenAtMs) || undefined}>
                          · {formatScreen(alert.screen)}
                        </span>
                      )}
                      {formatVersion(alert.appVersionName, alert.appVersionCode) && (
                        <span className="ml-2 text-gray-400">
                          · {formatVersion(alert.appVersionName, alert.appVersionCode)}
                        </span>
                      )}
                      {alert.suppressedCount > 0 && (
                        <span className="ml-2 text-amber-700">+{alert.suppressedCount} suppressed</span>
                      )}
                    </span>
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900"
                      title={`Hide all "${alertTypeKey(alert)}" alerts`}
                      onClick={(e) => {
                        e.stopPropagation();
                        f.hideType(alertTypeKey(alert));
                      }}
                    >
                      <EyeOff className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Single-alert detail view + mutations
// ────────────────────────────────────────────────────────────────────

function AlertDetailView({ alertId, familyId, familyName, familyEmail, onBack }) {
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const { email: effEmail, name: effName } = useFamilyIdentity(familyId, familyEmail, familyName);

  useEffect(() => {
    setLoading(true);
    const ref = doc(db, 'criticalAlerts', familyId, 'alerts', alertId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setAlert(null);
        } else {
          setAlert({ id: snap.id, familyId, ...snap.data() });
        }
        setLoading(false);
      },
      (err) => {
        console.error('AlertDetailView: listener error', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [familyId, alertId]);

  // mark-read / mark-unread / archive — see the shared helpers above for the
  // transaction semantics that keep the parent-doc unreadCount in sync.
  const markRead = useCallback(async () => {
    try { await markAlertRead(alert); }
    catch (e) { console.error('AlertsViewer: markRead failed', e); }
  }, [alert]);

  const markUnread = useCallback(async () => {
    try { await markAlertUnread(alert); }
    catch (e) { console.error('AlertsViewer: markUnread failed', e); }
  }, [alert]);

  const archive = useCallback(async () => {
    try {
      await archiveAlert(alert);
      onBack();
    } catch (e) {
      console.error('AlertsViewer: archive failed', e);
    }
  }, [alert, onBack]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!alert) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 py-16">
        <AlertTriangle className="w-12 h-12 mb-3 opacity-30" />
        <p>This alert no longer exists (probably archived from another tab).</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>Back</Button>
      </div>
    );
  }

  const t = tsMs(alert.tMs);
  const isUnread = !alert.readAt;
  const detailEntries = alert.detail ? Object.entries(alert.detail) : [];

  return (
    <>
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-shrink-0">{severityBadge(alert.severity)}</div>
          <h1 className="text-xl font-semibold truncate">
            {alert.subsystem}.{alert.reason}
          </h1>
          {isUnread
            ? <Badge className="bg-amber-500 hover:bg-amber-600">Unread</Badge>
            : <Badge variant="secondary">Read</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {isUnread ? (
            <Button variant="outline" size="sm" onClick={markRead}>
              <MailOpen className="w-4 h-4 mr-1" /> Mark as read
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={markUnread}>
              <Mail className="w-4 h-4 mr-1" /> Mark as unread
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={archive}>
            <Archive className="w-4 h-4 mr-1" /> Archive
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
          <div className="bg-white border border-gray-200 rounded-md p-4 space-y-2 text-sm">
            <Row label="When">
              <span className="font-mono">{formatAbsTime(t)}</span>
              <span className="text-muted-foreground ml-2">({formatAgo(t)})</span>
            </Row>
            <Row label="Severity"><span className="capitalize">{alert.severity}</span></Row>
            <Row label="Subsystem">{alert.subsystem}</Row>
            <Row label="Reason">{alert.reason}</Row>
            {formatScreen(alert.screen) ? (
              <Row label="Screen">
                <span>{formatScreen(alert.screen)}</span>
                {alert.screen !== formatScreen(alert.screen) && (
                  <span className="font-mono text-xs text-muted-foreground ml-2 select-text">{alert.screen}</span>
                )}
                {screenStalenessTitle(alert.tMs, alert.screenAtMs) && (
                  <span className="text-xs text-muted-foreground ml-2">({screenStalenessTitle(alert.tMs, alert.screenAtMs)})</span>
                )}
              </Row>
            ) : (
              <Row label="Screen">
                <span className="text-muted-foreground italic">Not recorded (alert pre-dates this field)</span>
              </Row>
            )}
            {formatVersion(alert.appVersionName, alert.appVersionCode) ? (
              <Row label="App version">{formatVersion(alert.appVersionName, alert.appVersionCode)}</Row>
            ) : (
              <Row label="App version">
                <span className="text-muted-foreground italic">Not recorded (alert pre-dates this field)</span>
              </Row>
            )}
            {detailEntries.length > 0 && (
              <Row label="Detail">
                <span className="font-mono text-xs select-text">
                  {detailEntries.map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join('  ')}
                </span>
              </Row>
            )}
            <Row label="Account">
              {effEmail
                ? <span className="select-text">{effEmail}</span>
                : <span className="text-muted-foreground italic">Unknown</span>}
            </Row>
            <Row label="Family">
              {effName ? (
                <span>
                  <span className="select-text">{effName}</span>
                  <span className="font-mono text-xs text-muted-foreground ml-2 select-text">{alert.familyId}</span>
                </span>
              ) : (
                <span className="font-mono text-xs select-text">{alert.familyId}</span>
              )}
            </Row>
            {alert.deviceId && alert.deviceId !== alert.familyId && (
              <Row label="Device">
                <span className="font-mono text-xs select-text">{alert.deviceId}</span>
              </Row>
            )}
            <Row label="Alert ID">
              <span className="font-mono text-xs select-text">{alert.id}</span>
            </Row>
            {alert.suppressedCount > 0 && (
              <Row label="Suppressed">
                <span className="text-amber-700">
                  {alert.suppressedCount} additional fire{alert.suppressedCount === 1 ? '' : 's'} dropped before this one
                </span>
              </Row>
            )}
            {alert.traceTruncated && (
              <Row label="Trace">
                <span className="text-amber-700">Truncated to fit size cap — oldest events dropped.</span>
              </Row>
            )}
            <Row label="Read state">
              {alert.readAt ? (
                <span>
                  <span className="text-gray-700">Read</span>
                  <span className="text-muted-foreground ml-2">
                    {formatAbsTime(tsMs(alert.readAt))}
                    {alert.readBy && <> by <span className="font-mono text-xs select-text">{alert.readBy}</span></>}
                  </span>
                </span>
              ) : (
                <span className="text-amber-700 font-medium">Unread</span>
              )}
            </Row>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-4">
            <div className="text-sm font-medium mb-3">Trace</div>
            <div className="select-text">
              <AlertTraceView trace={alert.trace} truncated={alert.traceTruncated} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-muted-foreground w-24 shrink-0">{label}:</span>
      <span className="flex-1 min-w-0 break-all">{children}</span>
    </div>
  );
}

export default AlertsViewer;
